import { useCallback, useEffect, useRef } from 'react';

interface UseInfiniteScrollOptions {
  /** When false, no observer is attached (e.g. nothing more to load). */
  enabled: boolean;
  /** Prefetch buffer — fires before the sentinel is fully on screen. */
  rootMargin?: string;
}

/**
 * Returns a ref callback to attach to a sentinel element near the bottom of a
 * list. When the sentinel scrolls within `rootMargin` of the viewport and the
 * hook is `enabled`, `onReachEnd` fires — letting the caller prefetch the next
 * batch before the user reaches the actual bottom.
 */
export function useInfiniteScroll(
  onReachEnd: () => void,
  { enabled, rootMargin = '600px' }: UseInfiniteScrollOptions,
): (node: Element | null) => void {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const onReachEndRef = useRef(onReachEnd);

  // Keep the latest callback without re-creating the observer each render.
  useEffect(() => {
    onReachEndRef.current = onReachEnd;
  }, [onReachEnd]);

  // Clean up on unmount.
  useEffect(() => () => observerRef.current?.disconnect(), []);

  return useCallback(
    (node: Element | null) => {
      observerRef.current?.disconnect();
      if (!node || !enabled || typeof IntersectionObserver === 'undefined') return;
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) onReachEndRef.current();
        },
        { rootMargin },
      );
      observerRef.current.observe(node);
    },
    [enabled, rootMargin],
  );
}
